import React, { Component } from 'react';
import { buildURL } from 'react-imgix';
import { debounce } from 'debounce';

import parseImgix from '@utils/parseImgix';
import { BackgroundImgixWrapperProps } from './BackgroundImgixWrapper.types';
import { Background, BackgroundWrapper, Content } from './BackgroundImgixWrapper.styles';

const imgixBaseParams = {
  fit: 'crop',
  crop: 'entropy',
  auto: 'format,compress',
};

export class BackgroundImgixWrapper extends Component<BackgroundImgixWrapperProps> {
  element: React.RefObject<HTMLDivElement>;

  handleResize: (event: Event) => void;

  static defaultProps = {
    isFullDim: false,
    withBlurredMobileBackground: false,
  };

  constructor(props) {
    super(props);
    this.element = React.createRef();
    this.setBackgroundImage = this.setBackgroundImage.bind(this);
    this.getImgixUrl = this.getImgixUrl.bind(this);
    this.handleResize = debounce(this.setBackgroundImage, 500);
  }

  componentDidMount() {
    const { src } = this.props;
    if (!src) {
      return;
    }
    this.element.current?.addEventListener('lazybeforeunveil', this.setBackgroundImage);
    window.addEventListener('resize', this.handleResize);
    this.element?.current?.classList.add('lazyload');
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
    this.element.current?.removeEventListener('lazybeforeunveil', this.setBackgroundImage);
  }

  setBackgroundImage() {
    if (this.element.current) {
      const imgixUrl = this.getImgixUrl();
      this.element.current.style.backgroundImage = `url(${imgixUrl})`;
    }
  }

  getImgixUrl() {
    const { src, baseParams } = this.props;

    const mergedParams = {
      ...imgixBaseParams,
      ...baseParams,
    };
    const size = this.getImageSize();
    const parsedUrl = parseImgix(src);
    return buildURL(parsedUrl, { ...size, ...mergedParams });
  }

  getImageSize() {
    return {
      w: this.element.current?.clientWidth,
      h: this.element.current?.clientHeight,
    };
  }

  render() {
    const {
      children,
      src,
      isFullDim,
      withBlurredMobileBackground,
      label,
      customHeight,
      hasTopBorderRadius,
    } = this.props;

    if (!src) {
      return children || null;
    }

    return (
      <BackgroundWrapper isFullDim={isFullDim} customHeight={customHeight}>
        <Background
          ref={this.element}
          role="img"
          aria-label={label}
          hasTopBorderRadius={hasTopBorderRadius}
        />
        <Content withBlurredMobileBackground={withBlurredMobileBackground}>{children}</Content>
      </BackgroundWrapper>
    );
  }
}
